import DeleteIcon from '@material-design-icons/svg/outlined/delete.svg?react';
import PhotoLibraryIcon from '@material-design-icons/svg/outlined/photo_library.svg?react';
import { type Either, left, right } from 'fp-ts/lib/Either';
import React, { useImperativeHandle, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { TypedError, typedError } from '../../lib/typedError';

interface Props {
  className?: string;
  maxSize?: number;
  name?: string;
  onChange?(
    file: Either<
      TypedError<'FileSizeExceeded' | 'InvalidInput'>,
      FileList[number]
    >,
  ): void;
  ref?: React.Ref<FileInputRefHandle>;
}

export interface FileInputRefHandle {
  click: () => void;
  clear: () => void;
}

export const FileInput = ({ ref, ...props }: Props) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);
  const internalInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    click: () => {
      internalInputRef.current?.click();
    },
    clear: () => {
      if (internalInputRef.current) {
        internalInputRef.current.value = '';
      }
      setPreviewUrl('');
      props.onChange?.(left(typedError('InvalidInput', 'Input cleared')));
    },
  }));

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.currentTarget;
    if ('files' in input && input.files?.length === 1) {
      const file = input.files[0];
      if (props.maxSize && file.size > props.maxSize) {
        if (internalInputRef.current) internalInputRef.current.value = '';
        setPreviewUrl('');
        props.onChange?.(
          left(typedError('FileSizeExceeded', 'File size is too large')),
        );
      } else {
        setPreviewUrl(URL.createObjectURL(file));
        props.onChange?.(right(file));
      }
    } else {
      if (internalInputRef.current) internalInputRef.current.value = '';
      setPreviewUrl('');
      props.onChange?.(left(typedError('InvalidInput', 'Invalid file input')));
    }
  };

  const handleInternalClear = () => {
    if (internalInputRef.current) {
      internalInputRef.current.value = '';
    }
    setPreviewUrl('');
    props.onChange?.(left(typedError('InvalidInput', 'Input cleared')));
  };

  const handleDragOver = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];

      const acceptedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/webp',
        'video/mp4',
      ];
      if (!acceptedTypes.includes(file.type)) {
        if (internalInputRef.current) internalInputRef.current.value = '';
        setPreviewUrl('');
        props.onChange?.(
          left(
            typedError(
              'InvalidInput',
              'Only image files (jpg, png, gif, webp) and MP4 videos are allowed',
            ),
          ),
        );
        return;
      }

      if (props.maxSize && file.size > props.maxSize) {
        if (internalInputRef.current) internalInputRef.current.value = '';
        setPreviewUrl('');
        props.onChange?.(
          left(typedError('FileSizeExceeded', 'File size is too large')),
        );
      } else {
        setPreviewUrl(URL.createObjectURL(file));
        props.onChange?.(right(file));
      }
    }
  };

  return (
    <label
      className={twMerge(
        'bg-center',
        'bg-contain',
        'bg-no-repeat',
        'block',
        'border-neutral-700',
        'border',
        'cursor-pointer',
        'flex-col',
        'flex',
        'items-center',
        'min-h-[116px]',
        'p-6',
        'relative',
        'rounded-xl',
        'text-white',
        'transition-all duration-200',
        'w-full',
        'focus:border-neutral-300',
        'hover:border-neutral-500',
        'placeholder:text-neutral-400',
        '[&:user-invalid]:border-rose-500',
        '[&:user-invalid]:focus:border-rose-500',
        '[&:user-invalid]:hover:border-rose-300',
        isDragOver && 'border-teal-500 bg-neutral-900 scale-[1.02]',
        props.className,
      )}
      style={{ backgroundImage: `url(${previewUrl})` }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="relative">
        <input
          accept=".jpg,.jpeg,.png,.gif,.webp,.mp4"
          aria-label={props.name ? `Upload ${props.name}` : 'Upload file'}
          className={twMerge(
            previewUrl ? 'hidden' : 'block',
            'bg-white',
            'h-10',
            'rounded-lg',
            'transition-colors',
            'w-36',
          )}
          name={props.name}
          ref={internalInputRef}
          type="file"
          onChange={handleInputChange}
        />
        {!previewUrl && (
          <div className="absolute top-0 left-0 right-0 bottom-0 flex items-center justify-center pointer-events-none">
            <PhotoLibraryIcon className="size-4 mr-1" />
            <div className="text-xs text-black">Choose a file</div>
          </div>
        )}
      </div>
      {!previewUrl && (
        <div className="text-xs text-white mt-2.5 pointer-events-none">
          jpg, png, gif, webp (3MB max), mp4 (30MB max)
        </div>
      )}
      {previewUrl && (
        <button
          type="button"
          className={twMerge(
            'absolute',
            'fill-white',
            'right-1',
            'top-1',
            'hover:fill-rose-500',
            'focus:fill-rose-500',
          )}
          onClick={handleInternalClear}
          aria-label="Remove selected file"
        >
          <DeleteIcon />
        </button>
      )}
    </label>
  );
};

FileInput.displayName = 'FileInput';
